import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCIcon, RCCInput, RCCRadio, RCCTooltip,RCCRefLovProps, RCCSelectOptionsCard } from "react-common-components";
import LovSectors from "src/components/Lov/Sectors";
import LovPeriodTypes from "src/components/Lov/TypePeriods";
import { useTranslation } from "src/context/Translations/context";
import Modals from "src/pages/Config/PeriodTypes/modals";
import { IRefTypePeriodsModals } from "src/pages/Config/PeriodTypes/interface";
import { useWatch } from "react-hook-form";
import Title from "src/components/Title";
import InputNumber from "src/components/InputNumber";
import LovTags from "src/components/Lov/Tags";
import LovUsers from "src/components/Lov/Users";
import {EvaluationForm, IIqtbc001, IndicatorLevel, ISimpleFormTab } from "../interface";
import { StrategicIcon } from "../styles";

const TabSimpleForm: React.FC<ISimpleFormTab> = ({ 
	indicator,
	goal,
	tags,
	register,
	control,
	errors,
	setValue,
	isDuplicate
}) => {
	const { translate} = useTranslation();
	const [data,setData] = useState<IIqtbc001>(indicator);
	const refModals = useRef<IRefTypePeriodsModals>(null);
	const lovRef = useRef<RCCRefLovProps>(null);

	const sharedInputs = useWatch({
		control,
		name: [
			"indicator.iqc001Descricao",
			"indicator.iqc001Nivel",
			"indicator.iqc001Iqb003Id",
			"indicator.iqc001Efa005Id",
			"indicator.iqc001Taa001Id",
			"indicator.iqc001Formaaval",
			"goal.iqc002Metadefault"
		]
	});

	useEffect(() => {
		setData(indicator);
	}, [indicator]);

	return(
		<RCCGrid container item justifyContent="center">
			<RCCGrid container item xs={12} sm={10} lg={8} >
				<RCCGrid container item>		
					<RCCInput 
						name="indicator.iqc001Descricao"
						register={register}
						label={translate("Descrição")}
						defaultValue={isDuplicate ? `(${translate("CÓPIA")}) ${sharedInputs[0]||data.iqc001Descricao}` : sharedInputs[0]||data.iqc001Descricao}
						error={errors?.indicator?.iqc001Descricao}
						maxLength={600}
						required
					/>
				</RCCGrid>
				<RCCGrid container item>
					<RCCSelectOptionsCard 
						name="indicator.iqc001Nivel"
						control={control}
						defaultValue={sharedInputs[1]?.toString()||data.iqc001Nivel?.toString()||IndicatorLevel.STRATEGIC}
						options={[
							{
								value:IndicatorLevel.STRATEGIC,
								label:translate("Estratégico"),
								icon:<StrategicIcon />
							},
							{
								value:IndicatorLevel.TATIC,
								label:translate("Tático"),
								icon:<RCCIcon name="chessKnight"/>
							},
							{
								value:IndicatorLevel.OPERATIONAL,
								label:translate("Operacional"),
								icon:<RCCIcon name="tools"/>
							}
						]}
						label={translate("Nível")}
					/>
				</RCCGrid>
				<RCCGrid container item
					wrap="nowrap"
					alignItems="center"
				>	
					<RCCGrid container item>	
						<LovPeriodTypes
							name="indicator.iqc001Iqb003Id"
							label={translate("Tipo de período")}
							control={control}
							error={errors?.indicator?.iqc001Iqb003Id}
							setValue={setValue}
							required
							defaultValue={sharedInputs[2]?.toString()||data.iqc001Iqb003Id?.toString()}
						/>
					</RCCGrid>
					<RCCTooltip
						text={translate("Novo tipo de período")}
						color="purple">
						<RCCButton 
							color="purple" 
							icon={{name:"plus"} }
							size="sm"
							type="button" 
							onClick={() => {refModals.current?.actionButtonAdd();}}
							styleInline="margin-top:22px;"/>
					</RCCTooltip>
					<Modals ref={refModals} onClose={(newId) => lovRef.current?.setValue(newId.toString())}/>
				</RCCGrid>
				<RCCGrid container item>		
					<LovUsers
						name="indicator.iqc001Efa005Id"
						label={translate("Responsável")}
						control={control}
						error={errors?.indicator?.iqc001Efa005Id}
						required
						defaultValue={sharedInputs[3]?.toString()||data.iqc001Efa005Id?.toString()}
					/>
				</RCCGrid>
				<RCCGrid container item>		
					<LovSectors
						name="indicator.iqc001Taa001Id"
						label={translate("Setor")}
						control={control}
						error={errors?.indicator?.iqc001Taa001Id}
						isCreatable
						defaultValue={sharedInputs[4]?.toString()||data.iqc001Taa001Id?.toString()}
						isClearable
					/>
				</RCCGrid>
				<RCCGrid container item>		
					<LovTags 
						control={control}
						name="tags"
						label={translate("Tags")}
						defaultValue={Array.isArray(tags) ? tags.join(", ") : tags}
						extraParametersOnLoad={{
							use: 2
						}}
						extraParametersOnCreate={{
							use: 2
						}}
						isClearable
					/>
				</RCCGrid>
				<RCCGrid container item>		
					<RCCRadio
						name="indicator.iqc001Formaaval"
						register={register}
						label={translate("Forma de avaliação")}
						color ="purple"
						styleInline="@media (max-width: 480px){
							display: block;
						}"
						error={errors?.indicator?.iqc001Formaaval}
						options={[
							{value:EvaluationForm.TO_UP,label:translate("Melhor para cima")},
							{value:EvaluationForm.TO_DOWN,label:translate("Melhor para baixo")}
						]}
						defaultValue={sharedInputs[5]?.toString()||data.iqc001Formaaval?.toString()||EvaluationForm.TO_UP}
					/>
				</RCCGrid>
				<hr/>
				<RCCGrid container item>
					<RCCGrid item>
						<Title 
							size="md" 
							color="purple" 
							marginTop={10}
						>
							{translate("Meta melhoria")}
						</Title>
					</RCCGrid>
					<RCCGrid container item>		
						<InputNumber 
							name="goal.iqc002Metadefault"
							control={control}
							allowNegative
							label={translate("Meta padrão")}
							defaultValue={sharedInputs[6]||goal?.iqc002Metadefault || 0}
							error={errors.goal?.iqc002Metadefault}
							maxLength={14}
							required
						/>
						<input 
							{...register("goal.iqc002Id")} 
							name="goal.iqc002Id"
							value={goal?.iqc002Id||0}
							type="hidden"
						/>
						<input 
							{...register("goal.iqc002Iqb001Id")} 
							name="goal.iqc002Iqb001Id"
							value={goal?.iqc002Iqb001Id||0}
							type="hidden"
						/>
					</RCCGrid>
				</RCCGrid>
			</RCCGrid>
		</RCCGrid>
	);
};
export default TabSimpleForm;
